import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import ScopeToggle from "../../components/ScopeToggle";
import { Inbox } from "lucide-react";

const TH = "text-left text-[10px] uppercase tracking-wider text-[#6B7280] px-2 py-1.5 border-b border-gray-200";
const TD = "px-2 py-1.5 text-xs text-[#0E2240] border-b border-gray-100";

export default function ZonalDashboard() {
  const { user } = useAuth();
  const [mode, setMode] = useState("Live");
  const [d, setD] = useState(null);

  useEffect(() => {
    setD(null);
    api.get("/analytics/zonal", { params: { mode } }).then(({ data }) => setD(data)).catch(() => {});
  }, [mode]);

  return (
    <div data-testid="zonal-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#6B7280]">Zone Command</div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#0E2240] tracking-tight mt-1">Welcome, {user?.full_name?.split(" ")[0]}</h1>
        </div>
        <ScopeToggle value={mode} onChange={setMode} />
      </div>

      {d?.pending_with_me > 0 && (
        <Link to="/requisitions/inbox" data-testid="zonal-pending-tile"
          className="flex items-center gap-3 bg-white border border-[#D97706]/60 rounded-sm p-4 mb-6 hover:border-[#0E2240] transition-colors duration-200">
          <Inbox size={20} className="text-[#D97706]" strokeWidth={1.75} />
          <div>
            <div className="text-sm font-semibold text-[#0E2240]">{d.pending_with_me} requisition{d.pending_with_me > 1 ? "s" : ""} waiting on your approval</div>
            <div className="text-[11px] text-[#6B7280]">The clock is running against you — open the MRF Approvals inbox</div>
          </div>
        </Link>
      )}

      <div className="bg-white border border-gray-200 rounded-sm p-4 sm:p-5 mb-8" data-testid="zonal-branch-table">
        <h2 className="font-serif text-base text-[#0E2240] mb-3">Branch manning league — worst first</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr>
              <th className={TH}>Branch</th><th className={TH}>Fill</th><th className={TH}>Sanctioned</th><th className={TH}>Active</th>
              <th className={TH}>Shortfall</th><th className={TH}>Surplus</th><th className={TH}>Open Reqs</th><th className={TH}>Oldest Open</th>
            </tr></thead>
            <tbody>
              {(d?.branches || []).map((b) => {
                const fillPct = b.fill != null ? b.fill * 100 : null;
                const barColor = fillPct == null ? "#6B7280" : fillPct >= 95 ? "#1B7F3B" : fillPct >= 85 ? "#D97706" : "#BB162B";
                return (
                  <tr key={b.branch_id} data-testid={`zonal-branch-${b.branch_id}`}>
                    <td className={`${TD} font-medium`}>{b.branch_name}</td>
                    <td className={TD}>
                      <div className="flex items-center gap-2 min-w-[120px]">
                        <div className="flex-1 h-2 bg-gray-100 rounded-sm overflow-hidden">
                          <div className="h-full" style={{ width: `${Math.min(100, fillPct || 0)}%`, backgroundColor: barColor }} />
                        </div>
                        <span className="tabular-nums text-[11px] w-11" style={{ color: barColor }}>{fillPct != null ? `${fillPct.toFixed(1)}%` : "—"}</span>
                      </div>
                    </td>
                    <td className={`${TD} tabular-nums`}>{b.sanctioned}</td>
                    <td className={`${TD} tabular-nums`}>{b.active}</td>
                    <td className={`${TD} tabular-nums ${b.shortfall > 0 ? "text-[#BB162B] font-semibold" : ""}`}>{b.shortfall}</td>
                    <td className={`${TD} tabular-nums ${b.surplus > 0 ? "text-[#1E5AA8]" : ""}`}>{b.surplus}</td>
                    <td className={`${TD} tabular-nums`}>{b.open_reqs}</td>
                    <td className={`${TD} tabular-nums ${b.oldest_open_days > 45 ? "text-[#BB162B] font-semibold" : ""}`}>
                      {b.oldest_open_days != null ? `${b.oldest_open_days}d` : "—"}
                    </td>
                  </tr>
                );
              })}
              {d && d.branches.length === 0 && <tr><td colSpan={8} className="px-2 py-8 text-center text-xs text-[#6B7280]">No branches in scope</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
